import { Observable, Subject } from 'rxjs'
import { map, shareReplay, take, takeUntil, tap } from 'rxjs/operators'

import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { Router } from '@angular/router'
import { FileReference } from '@classes/FileReference'
import { AssetSearchResult, AssetService, FileSearchResult } from '@services/asset.service'
import { FileReferenceService } from '@services/file-reference.service'
import { RightSidebarService } from '@services/right-sidebar.service'
import { SearchNavigationService } from '@services/search-navigation.service'
import { SearchService } from '@services/search.service'

@Component({
    selector: 'shared-search-modal',
    templateUrl: './search-modal.component.html',
    styleUrls: ['./search-modal.component.css'],
    standalone: false
})
export class SearchModalComponent implements OnDestroy, OnInit {
    public searchControl = new FormControl('')
    public thumbnailUrls: { [resultKey: string]: Observable<string> } = {}
    public selectedAssetId: number | null = null
    public selectedFileId: number | null = null
    public selectedPreviewUrl: Observable<string> | null = null
    public selectedS3Id: number | null = null
    public showFileDetailsPanel = false

    private _destroy$ = new Subject<void>()
    private _objectUrls = new Map<string, string>()

    get error$() { return this._searchService.error$ }
    get loading$() { return this._searchService.loading$ }

    // Create results$ as a proper observable, not a getter that creates new pipes
    public results$: Observable<(AssetSearchResult | FileSearchResult)[]>

    constructor(
        private _assetService: AssetService,
        private _dialogRef: MatDialogRef<SearchModalComponent>,
        private _fileReferenceService: FileReferenceService,
        private _rightSidebarService: RightSidebarService,
        private _router: Router,
        private _searchNavigationService: SearchNavigationService,
        private _searchService: SearchService,
    ) {
        // Initialize results$ in constructor, not as a getter
        this.results$ = this._searchService.results$.pipe(
            map(results => {
                const activeKeys = new Set(results.map(result => this.getResultKey(result)))
                this.pruneThumbnailUrls(activeKeys)

                // Generate thumbnail URLs for file results that are images
                results.forEach(result => {
                    if (this.isFileResult(result) && this.isImageFile(result.extension)) {
                        const key = this.getFileResultKey(result)
                        if (!this.thumbnailUrls[key]) {
                            this.thumbnailUrls[key] = this.getFileThumbnailUrl(result)
                        }
                    } else if (!this.isFileResult(result)) {
                        const key = this.getAssetThumbnailKey(result)
                        // Asset results — prefer EFS thumbnail, fall back to S3 presigned URL
                        if (!this.thumbnailUrls[key]) {
                            if (result.thumbnailFileId && result.thumbnailHash) {
                                const fileRef = new FileReference(result.thumbnailFileId, undefined, undefined, { hash: result.thumbnailHash })
                                this.thumbnailUrls[key] = this.trackObjectUrl(
                                    key,
                                    this._fileReferenceService.getBlobUrlForFileReference(fileRef, result.id, 'asset')
                                )
                            } else if (result.thumbnailS3Id) {
                                this.thumbnailUrls[key] = this.cacheUrl(
                                    this._fileReferenceService.getS3PresignedUrl(result.thumbnailS3Id)
                                )
                            }
                        }
                    }
                })
                return results
            })
        )
    }

    ngOnInit(): void {
        // Reset search state when opening the modal
        this._searchService.reset()

        // Restore last search query if available and trigger search
        const lastQuery = this._searchService.lastQuery
        if (lastQuery) {
            this.searchControl.setValue(lastQuery, { emitEvent: false })
            this._searchService.searchAssets(lastQuery)
        } else {
            // Trigger initial empty search to clear any stale state
            this._searchService.searchAssets('')
        }

        this.searchControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(value => this._searchService.searchAssets(value ?? ''))
    }

    /** Unique key for file search results (EFS fileId or S3 ref ID) */
    private getFileResultKey(result: FileSearchResult): string {
        return result.fileId != null
            ? `file-${result.fileId}`
            : `s3-${result.s3ObjectReferenceId ?? 0}`
    }

    private getAssetThumbnailKey(result: AssetSearchResult): string {
        if (result.thumbnailFileId != null) return `file-${result.thumbnailFileId}`
        if (result.thumbnailS3Id != null) return `s3-${result.thumbnailS3Id}`
        return `asset-${result.id}`
    }

    private getResultKey(result: AssetSearchResult | FileSearchResult): string {
        return this.isFileResult(result)
            ? this.getFileResultKey(result)
            : this.getAssetThumbnailKey(result)
    }

    getThumbnailUrl(result: AssetSearchResult | FileSearchResult): Observable<string> | null {
        if (this.isFileResult(result)) {
            return this.thumbnailUrls[this.getFileResultKey(result)] || null
        }
        return this.thumbnailUrls[this.getAssetThumbnailKey(result)] || null
    }

    getFileThumbnailUrl(result: FileSearchResult): Observable<string> {
        if (result.s3ObjectReferenceId) {
            return this.cacheUrl(this._fileReferenceService.getS3PresignedUrl(result.s3ObjectReferenceId))
        }
        const key = this.getFileResultKey(result)
        return this.trackObjectUrl(
            key,
            this._fileReferenceService.getBlobUrlForFileReference(
                new FileReference(result.fileId, undefined, undefined, { hash: result.fileHash }),
                result.assetId,
                'asset'
            )
        )
    }

    private trackObjectUrl(key: string, source$: Observable<string>): Observable<string> {
        return source$.pipe(
            takeUntil(this._destroy$),
            tap(url => {
                const previousUrl = this._objectUrls.get(key)
                if (previousUrl && previousUrl !== url) URL.revokeObjectURL(previousUrl)
                this._objectUrls.set(key, url)
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        )
    }

    private cacheUrl(source$: Observable<string>): Observable<string> {
        return source$.pipe(
            takeUntil(this._destroy$),
            shareReplay({ bufferSize: 1, refCount: true })
        )
    }

    private pruneThumbnailUrls(activeKeys: Set<string>): void {
        Object.keys(this.thumbnailUrls).forEach(key => {
            if (activeKeys.has(key)) return

            delete this.thumbnailUrls[key]
            const objectUrl = this._objectUrls.get(key)
            if (objectUrl) URL.revokeObjectURL(objectUrl)
            this._objectUrls.delete(key)
        })
    }

    isFileResult(result: AssetSearchResult | FileSearchResult): result is FileSearchResult {
        return 'fileId' in result
    }

    isImageFile(extension: string): boolean {
        const ext = extension?.toLowerCase() || ''
        return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'].includes(ext)
    }

    getFileIcon(extension: string): string {
        const ext = extension?.toLowerCase() || ''

        if (this.isImageFile(extension)) {
            return 'fa-image'
        } else if (['.pdf'].includes(ext)) {
            return 'fa-file-pdf'
        } else if (['.doc', '.docx'].includes(ext)) {
            return 'fa-file-word'
        } else if (['.glb', '.gltf', '.obj', '.fbx'].includes(ext)) {
            return 'fa-cube'
        } else {
            return 'fa-file'
        }
    }

    clearSearch() {
        this.searchControl.setValue('')
    }

    close() {
        this._searchNavigationService.clearSearchContext()
        this._dialogRef.close()
    }

    onResultClick(result: AssetSearchResult | FileSearchResult) {
        if (this.isFileResult(result)) {
            this.selectedAssetId = result.assetId ?? null
            this.selectedPreviewUrl = this.isImageFile(result.extension)
                ? this.getThumbnailUrl(result)
                : null
            if (result.s3ObjectReferenceId && !result.fileId) {
                this.selectedS3Id = result.s3ObjectReferenceId
                this.selectedFileId = null
            } else {
                this.selectedFileId = result.fileId
                this.selectedS3Id = null
            }
            this.showFileDetailsPanel = true
        } else {
            this.navigateToAsset(result)
        }
    }

    onFileDetailsPanelClosed() {
        this.showFileDetailsPanel = false
        this.selectedAssetId = null
        this.selectedFileId = null
        this.selectedPreviewUrl = null
        this.selectedS3Id = null

        // Check if we should also close the search modal (when navigating to asset)
        const navigationState = this._searchNavigationService.currentState
        if (navigationState.fromSearch) {
            this._dialogRef.close()
        }
    }

    navigateToAsset(result: AssetSearchResult) {
        // Save search context before navigating
        const currentQuery = this.searchControl.value || this._searchService.lastQuery
        this._searchNavigationService.saveSearchContext(currentQuery)

        // Navigate first
        this._router.navigate(['/assets', result.assetTypeID], {
            queryParams: { assetID: result.id, fromSearch: 'true' },
            queryParamsHandling: 'merge' // Preserve other query params if any
        }).then(() => {
            // Load and open the asset in the edit panel
            this._assetService.setSelectedAssetByID(result.id).pipe(take(1)).subscribe({
                next: () => {
                    this._rightSidebarService.openPanel('Edit Asset')
                    this._dialogRef.close()
                },
                error: (err) => {
                    console.error('[SearchModal] Error loading asset:', err)
                    this._dialogRef.close() // Close even on error
                }
            })
        })
    }

    ngOnDestroy(): void {
        this._destroy$.next()
        this._destroy$.complete()
        this._objectUrls.forEach(url => URL.revokeObjectURL(url))
        this._objectUrls.clear()
    }
}
